import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as ActionApprove } from '../../assets/icons/action-approve.svg';
import { ReactComponent as DeviceStatusIcon } from '../../assets/icons/dot-circle.svg';
import { ReactComponent as SearchIcon } from '../../assets/icons/search-icon.svg';
import {
  WebsocketListingProperties,
  WebsocketListingRequest,
} from '../../interface/blocked-neverblocked.interface';
import { DeviceListingProperties } from '../../interface/device-management.interface';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  fetchNeverBlockedWebsocket,
  fetchNeverBlockedWebsocketFilters,
  setVlbcSearchBy,
  setSelectedDevice,
} from '../../redux/slices/DeviceNeverBlockedWebsocketSlice';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import CustomInput from '../../shared-components/custom-input/CustomInput';
import CustomTable from '../../shared-components/custom-table/CustomTable';
import PageTitle from '../../shared-components/page-title/PageTitle';
import DeviceSidePanel from '../../shared-components/table-side-panel/DeviceSidePanel';
import { GetParametersFromCommonFilters } from '../../shared-functions/CommonFilters';
import deviceBlock from '../../utils/api/device';

interface CheckBoxProperties {
  leftTopContent?: JSX.Element;
}

/**
 * Component to display list of devices Never Blocked Web Socket
 * @returns
 */
function NeverBlockedwebsocket(properties: CheckBoxProperties) {
  const { t } = useTranslation('devices');
  const navigate = useNavigate();
  const { leftTopContent } = properties;
  const [valueToShow, setValueToShow] = useState('');
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const dispatch = useAppDispatch();
  const { devices, limit, page, loading, totalPages, totalResults, filters } = useAppSelector(
    (state) => state.deviceNeverBlocked,
  );

  /**
   * Handle the fetch the data in api function
   * @param data
   */
  const fetchData = (data: WebsocketListingRequest) => {
    if (devices.length === 0 || !loading) {
      dispatch(fetchNeverBlockedWebsocket(data));
    }
  };

  /**
   * UI of Status
   * @param device
   * @returns
   */
  const renderStatus = (device: WebsocketListingProperties) =>
    device?.isDeviceOffline !== undefined ? (
      <div>
        <div className={device.isDeviceOffline ? 'tag-danger-label' : 'tag-success-label'}>
          <DeviceStatusIcon />
          <span style={{ fontWeight: '700' }}>{device.isDeviceOffline ? t('offline') : t('online')}</span>
        </div>
      </div>
    ) : (
      <div />
    );

  /**
   * Function to open device view model
   */
  const handleOpenModelView = (chargingPoint: DeviceListingProperties, newTab: boolean) => {
    if (chargingPoint) {
      dispatch(
        setSelectedDevice({
          id: chargingPoint?.id,
          name: chargingPoint?.chargingPointName,
          chargingPointSn: chargingPoint?.chargingPointSn,
          modelSeries: chargingPoint?.modelSeries,
        }),
      );

      if (newTab) {
        const url = `/ev-admin/device-management/device/${chargingPoint.id}/device-details?searchBy=${chargingPoint.chargingPointName}`;
        window.open(url, '_blank'); // opens in a new tab
      } else {
        navigate({
          pathname: `/ev-admin/device-management/device/${chargingPoint.id}/device-details`,
          search: `?searchBy=${chargingPoint.chargingPointName}`,
        });
      }
    } else {
      console.log(t('erroropeningSeparatePage'));
    }
  };

  /**
   * Handling reject warning
   * @param payouts
   */
  const handleNeverBlock = async (rowId?: string | null) => {
    if (!rowId) return;
    try {
      await deviceBlock.deleteNeverBlockCp(rowId);
      fetchData({
        page,
        limit,
      });
      dispatch(
        setNotificationMessage({
          message:t('deviceRemovedNeverBlock'),
          status: 200,
          type: 'success',
        }),
      );
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || 'Something went wrong!',
          status: error?.response?.status || 500,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
  };

  const column = [
    {
      label: t('deviceName'),
      accessorKey: 'chargingPointName' as keyof WebsocketListingProperties,
      sortAllow: false,
      queryParamsKey: 'deviceId',
      sidePanelComponent: <DeviceSidePanel fetchIdFrom="queryParams" />,
      handleClickExpandView: handleOpenModelView,
    },
    {
      label: t('deviceSN'),
      queryParamsKey: 'deviceId',
      sidePanelComponent: <DeviceSidePanel fetchIdFrom="queryParams" />,
      accessorKey: 'chargingPointSn' as keyof WebsocketListingProperties,
      sortAllow: false,
      handleClickExpandView: handleOpenModelView,
      hideWhenSidePanelOpen: true,
    },
    {
      label: t('deviceModel'),
      accessorKey: 'chargePointModel' as keyof WebsocketListingProperties,
      sortAllow: false,
      hideWhenSidePanelOpen: true,
    },
    {
      label:t('maxPowerOutput'),
      accessorKey: 'maximumOutputPower' as keyof WebsocketListingProperties,
      sortAllow: false,
      /**
       * Data custom function
       * @param currentRow
       */
      customData: (currentRow: WebsocketListingProperties) => (
        <p>
          {currentRow.maximumOutputPower} {currentRow.maximumOutputPowerUnit}
        </p>
      ),
    },
    {
      label: t('deviceStatus'),
      accessorKey: 'isDeviceOffline' as keyof WebsocketListingProperties,
      customData: renderStatus,
      sortAllow: false,
    },
    {
      label: t('canBlock'),
      accessorKey: 'block' as keyof WebsocketListingProperties,
      columnWidth: 80,
      customData:
        /**
         * function for action buttons
         * @param currentRow
         * @returns
         */
        (currentRow: WebsocketListingProperties) => (
          <div className="action-button-group">
            <div className="action-button-item view-color">
              <ActionApprove onClick={() => handleNeverBlock(currentRow.chargingPointSn)} />
            </div>
          </div>
        ),
    },
  ];

  // Handle input change
  /**
   * Handles changes to the filter input and updates the search value.
   * @param name - The name of the filter.
   * @param value - The value to filter by.
   */
  const handleChangeFilters = (name: string, value: string) => {
    setValueToShow(value);
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      dispatch(fetchNeverBlockedWebsocket({ searchBy: value }));
    }, 1000);
    dispatch(setVlbcSearchBy(value));
  };

  useEffect(() => {
    if (devices.length === 0) {
      const queryParameters = GetParametersFromCommonFilters(filters);

      dispatch(
        fetchNeverBlockedWebsocket({
          page,
          limit,
          ...queryParameters,
        }),
      );
    }
  }, [filters]);

  /**
   * Table pagination function
   * @param value
   */
  const pageNateLimitChange = (value: WebsocketListingRequest) => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    dispatch(fetchNeverBlockedWebsocket({ ...value, ...queryParameters }));
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(fetchNeverBlockedWebsocket({ ...queryParameters, page, limit }));
  };

  return (
    <div>
      <PageTitle title={t('websocketManagement')} />
      <CustomTable
        header={column}
        data={devices}
        serialNo
        filter={{}}
        filterClearFunction={() => {}}
        filterFunction={() => {}}
        actionButton={false}
        limit={limit}
        page={page}
        loading={loading}
        totalPages={totalPages}
        totalResults={totalResults}
        pageLimitChange={(value: any) => pageNateLimitChange(value)}
        sizeChangeButtonRequired
        // rowClickEvent={handleClickTableRow}
        filters={filters}
        refreshRequired
        handleRefresh={handleRefresh}
        hoverLabel="Refresh"
        filterListToDispatch={fetchNeverBlockedWebsocketFilters}
        leftTopContent={
          <>
            <div>
              <CustomInput
                inputLabel=""
                type="text"
                placeholder={t('searchHere')}
                name="searchBy"
                svgIcon={<SearchIcon />}
                value={valueToShow}
                onChangeValue={(_, value) => handleChangeFilters('searchBy', String(value))}
              />
            </div>
            {leftTopContent}
          </>
        }
      />
    </div>
  );
}

export default NeverBlockedwebsocket;
